/**
 * mergeDetailContacts — the contacts a detail page shows for one connection,
 * and which of them the viewer may not edit. One home for the rule both the
 * old Detail page and the new Detail engine use (signup D1; lifted verbatim
 * from ProfileConnectionDetailPage).
 *
 * CONTACTS — claiming is the gate (ruling 2026-07-18): you can add and edit
 * contact fields for someone who hasn't claimed them; a field is claimed the
 * moment its owner enters it themselves. isActiveRoyalePeer IS that boundary —
 * an Anon peer has no senderProfileUserId and conversion stamps one, so
 * conversion is the bulk-claim, with no migration and no new state.
 *
 * @param {object} p
 * @param {object} p.userData - the peer's record (anonData)
 * @param {object} p.userContacts - the viewer's own entries (connection_views.user_contacts)
 * @param {boolean} p.peerHasClaimed - isActiveRoyalePeer(userData)
 * @param {string[]} p.contactKeys - ALL_CONTACT_KEYS
 * @returns {{ mergedContacts: object, readOnlyContactKeys: string[], peerShared: object }}
 */
/**
 * The contacts a peer's record has SHARED (sharedFields is the filter, never
 * value presence) — the one rule; mergeDetailContacts and the guest's view of
 * the host card both read it.
 *
 * @param {object} userData - the peer's record
 * @param {string[]} contactKeys - ALL_CONTACT_KEYS
 * @returns {object} key → value
 */
export const peerSharedContacts = (userData, contactKeys) => {
  const sharedFields = userData?.sharedFields || [];
  const shared = {};
  for (const field of contactKeys) {
    if (sharedFields.includes(field) && userData?.[field]) shared[field] = userData[field];
  }
  return shared;
};

export const mergeDetailContacts = ({ userData, userContacts, peerHasClaimed, contactKeys }) => {
  // Keep ruling (CD #15): a peer who is GONE — account-deleted OR left this
  // connection — KEEPS their shared contacts on your copy; you take full
  // ownership (they render and become editable). We no longer drop them. That's
  // driven by peerHasClaimed, which is already false for a gone peer
  // (isActiveRoyalePeer → isPeerGone): nothing is locked, so the viewer can edit
  // or clear each kept field. (Pre-CD #15 this filtered the peer's fields out for
  // a deleted profile — that contradicted "you keep what they shared".)
  const own = userContacts || {};
  const peerShared = peerSharedContacts(userData, contactKeys);
  // Claimed: the peer's declaration wins and locks. Unclaimed: the viewer's own
  // entry wins, so a correction they type is the one they see — including an
  // empty override, which is how the X clears a value the peer declared.
  const mergedContacts = peerHasClaimed ? { ...own, ...peerShared } : { ...peerShared, ...own };
  // Read-only = the peer's specific shared field keys (e.g. 'email'), so the
  // owner's own additions — including a second of the same type ('email2') —
  // stay editable. Nothing is read-only until the peer claims: an Anon's
  // declared phone is theirs to state, not theirs to own, until they convert.
  const readOnlyContactKeys = peerHasClaimed ? Object.keys(peerShared) : [];
  return { mergedContacts, readOnlyContactKeys, peerShared };
};
